import { useAppSelector } from "integrations/redux/hooks"
import { selectMapFeatures } from "./selectors"
import { useDispatch } from "react-redux"
import { ContrastTypes, mapStyles, MapStyleNames } from "./module"
import { mapBaseLayerByKey } from "@nova/constants"
import {
    LightMapIcon,
    SatelliteMapIcon,
    WhiteMapIcon,
    DarkMapIcon,
} from "themes/default-tailwind/icons"
import {
    setMapLabels as setMapLabelsAction,
    setContours as setContoursAction,
    setShading as setShadingAction,
    setBuildings as setBuildingsAction,
} from "../control/slice"
import { setGlobalTerrainData, useGlobalTerrainData } from "../GlobalTerrainManager"
import { setLocalStorageValue, useLocalStorageValue } from "tools/store/localStorageRegistry"

export type MapBackgroundViewType = "project" | "overview"

const localStorageKeyByViewType = {
    project: "mapBackground",
    overview: "overviewMapBackground",
} as const

type MapStyleToggle = {
    label: string
    val: boolean
    flip: () => void
}

export function useMapFeatures(): MapStyleToggle[] {
    const dispatch = useDispatch()
    const { mapLabels, contours, shading, buildings } = useAppSelector(selectMapFeatures)

    return [
        {
            label: "Map Labels",
            val: mapLabels,
            flip: () => dispatch(setMapLabelsAction(!mapLabels)),
        },
        {
            label: "Contours",
            val: contours,
            flip: () => dispatch(setContoursAction(!contours)),
        },
        {
            label: "Shading",
            val: shading,
            flip: () => dispatch(setShadingAction(!shading)),
        },
        {
            label: "Buildings",
            val: buildings,
            flip: () => dispatch(setBuildingsAction(!buildings)),
        },
    ]
}

export function useMapTerrain(): MapStyleToggle {
    const { terrainState } = useGlobalTerrainData(["terrainState"])

    return {
        label: "Terrain",
        val: terrainState,
        flip: () => setGlobalTerrainData({ terrainState: !terrainState }),
    }
}
export function useMapBackground(viewType: MapBackgroundViewType = "project") {
    const localStorageKey = localStorageKeyByViewType[viewType]
    const background = useLocalStorageValue(localStorageKey)
    const setBackground = (value: MapStyleNames) => setLocalStorageValue(localStorageKey, value)
    return {
        background,
        mapBackgrounds: [
            {
                label: MapStyleNames.Light,
                title: mapBaseLayerByKey[MapStyleNames.Light],
                icon: LightMapIcon,
                contrast: ContrastTypes.Light,
                isActiveBackground: background === MapStyleNames.Light,
                switchTo: () => setBackground(MapStyleNames.Light),
            },
            {
                label: MapStyleNames.Satellite,
                title: mapBaseLayerByKey[MapStyleNames.Satellite],
                icon: SatelliteMapIcon,
                contrast: ContrastTypes.Dark,
                isActiveBackground: background === MapStyleNames.Satellite,
                switchTo: () => setBackground(MapStyleNames.Satellite),
            },
            {
                label: MapStyleNames.White,
                title: mapBaseLayerByKey[MapStyleNames.White],
                icon: WhiteMapIcon,
                contrast: ContrastTypes.Light,
                isActiveBackground: background === MapStyleNames.White,
                switchTo: () => setBackground(MapStyleNames.White),
            },
            {
                label: MapStyleNames.Dark,
                title: mapBaseLayerByKey[MapStyleNames.Dark],
                icon: DarkMapIcon,
                contrast: ContrastTypes.Dark,
                isActiveBackground: background === MapStyleNames.Dark,
                switchTo: () => setBackground(MapStyleNames.Dark),
            },
        ],
        isLight: mapStyles[background].contrast === ContrastTypes.Light,
    }
}
